import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AuditLogDisplay } from './AuditLogDisplay';
import { AuditLogFilters } from './AuditLogFilters';
import { EntityAuditTrail } from './EntityAuditTrail';
import { AuditAction, AuditLogQuery } from '../../types/audit';
import { auditService } from '../../services/auditService';
import { asNumber, asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { Button, EmptyState, Input, PageHeader, SegmentedControl, Select, StatGrid } from '../ui';
import { History } from 'lucide-react';
import { ENTITY_TYPES } from './auditFormat';

interface AuditDashboardProps {
  className?: string;
}

const VIEWS = ['logs', 'entity-trail'] as const;
type ViewMode = (typeof VIEWS)[number];

const STATS_DAYS = 7;
const DEFAULT_LIMIT = 50;

/* One codec per parameter, made once: the URL state reads them on every render */
const VIEW = asOneOf(VIEWS);
const ACTION = asOneOf<AuditAction | ''>(Object.values(AuditAction));
const PAGE = asNumber(1);
const LIMIT = asNumber(1, 500);

/** The stats endpoint returns counts keyed by action */
type ActionCounts = Partial<Record<AuditAction, number>>;

export const AuditDashboard: React.FC<AuditDashboardProps> = ({
  className = '',
}) => {
  /*
   * The view, every filter and the page are in the address bar, so a link shows
   * the same slice of the log — and an audit trail is a link worth sending.
   */
  const [viewMode, setViewMode] = useUrlState<ViewMode>('view', 'logs', VIEW);
  const [page] = useUrlState('page', 1, PAGE);
  const [limit] = useUrlState('limit', DEFAULT_LIMIT, LIMIT);
  const [action] = useUrlState<AuditAction | ''>('action', '', ACTION);
  // `entityType` and `entityId` name the entity whose trail is on screen, so
  // the log's own entity filter answers to a name of its own
  const [logEntityType] = useUrlState('logEntityType', '');
  const [logEntityId] = useUrlState('logEntityId', '');
  const [userId] = useUrlState('userId', '');
  const [from] = useUrlState('from', '');
  const [to] = useUrlState('to', '');
  const [trailType] = useUrlState('entityType', '');
  const [trailId] = useUrlState('entityId', '');

  const query = useMemo<AuditLogQuery>(
    () => ({
      page,
      limit,
      action: action || undefined,
      entityType: logEntityType || undefined,
      entityId: logEntityId || undefined,
      userId: userId || undefined,
      startDate: from || undefined,
      endDate: to || undefined,
    }),
    [page, limit, action, logEntityType, logEntityId, userId, from, to],
  );

  // The lookup fields are a draft until the form is submitted; only the entity
  // actually being read belongs in the URL
  const [entityType, setEntityType] = useState(trailType);
  const [entityId, setEntityId] = useState(trailId);
  const trail = trailType && trailId ? { entityType: trailType, entityId: trailId } : null;
  const [counts, setCounts] = useState<ActionCounts | null | 'error'>(null);

  useEffect(() => {
    let cancelled = false;
    auditService
      .getActivityStats(STATS_DAYS)
      .then(response => {
        if (!cancelled) setCounts(((response?.stats ?? {}) as unknown) as ActionCounts);
      })
      .catch(() => {
        if (!cancelled) setCounts('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * The filters hand back a whole query, with `page` already back at one, so
   * the eight parameters move as one change.
   */
  const write = useUrlWriter();
  const handleQueryChange = useCallback(
    (newQuery: AuditLogQuery) => {
      write({
        page: newQuery.page && newQuery.page > 1 ? String(newQuery.page) : null,
        limit: newQuery.limit && newQuery.limit !== DEFAULT_LIMIT ? String(newQuery.limit) : null,
        action: newQuery.action ?? null,
        logEntityType: newQuery.entityType ?? null,
        logEntityId: newQuery.entityId ?? null,
        userId: newQuery.userId ?? null,
        from: newQuery.startDate ?? null,
        to: newQuery.endDate ?? null,
      });
    },
    [write],
  );

  const handleEntityTrailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (entityType && entityId.trim()) {
      write({ view: 'entity-trail', entityType, entityId: entityId.trim() });
    }
  };

  const count = (...actions: AuditAction[]) =>
    counts && counts !== 'error' ? actions.reduce((sum, action) => sum + (Number(counts[action]) || 0), 0).toLocaleString() : '—';

  const totalEvents =
    counts && counts !== 'error'
      ? Object.values(counts)
          .reduce((sum: number, value) => sum + (Number(value) || 0), 0)
          .toLocaleString()
      : '—';

  return (
    <div className={className}>
      <PageHeader
        title="Audit"
        actions={
          <SegmentedControl<ViewMode>
            label="View"
            value={viewMode}
            onChange={setViewMode}
            items={[
              { value: 'logs', label: 'Log' },
              { value: 'entity-trail', label: 'Trail' },
            ]}
          />
        }
      />

      {viewMode === 'logs' ? (
        <div className="space-y-4">
          {counts !== 'error' && (
            <StatGrid
              items={[
                { label: `Events · ${STATS_DAYS}d`, value: totalEvents },
                { label: `Changes · ${STATS_DAYS}d`, value: count(AuditAction.CREATE, AuditAction.UPDATE, AuditAction.DELETE) },
                { label: `Reviews · ${STATS_DAYS}d`, value: count(AuditAction.APPROVE, AuditAction.REJECT) },
                { label: `Sign-ins · ${STATS_DAYS}d`, value: count(AuditAction.LOGIN) },
              ]}
            />
          )}
          <AuditLogFilters query={query} onQueryChange={handleQueryChange} />
          <AuditLogDisplay initialQuery={query} showFilters={false} showExport={true} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Entity lookup */}
          <form onSubmit={handleEntityTrailSubmit} className="flex flex-wrap items-center gap-2">
            <Select
              id="entityType"
              aria-label="Entity type"
              value={entityType}
              onChange={e => setEntityType(e.target.value)}
              required
              className="w-full sm:w-44"
            >
              <option value="">Entity type</option>
              {ENTITY_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
            <Input
              id="entityId"
              aria-label="Entity ID"
              value={entityId}
              onChange={e => setEntityId(e.target.value)}
              required
              placeholder="Entity ID"
              spellCheck={false}
              autoComplete="off"
              className="min-w-0 flex-1 font-ui-mono sm:max-w-sm"
            />
            <Button type="submit" variant="dark" disabled={!entityType || !entityId.trim()} className="h-10 sm:h-9">
              View
            </Button>
          </form>

          {trail ? (
            <EntityAuditTrail entityType={trail.entityType} entityId={trail.entityId} />
          ) : (
            <div className="rounded-2xl border border-dashed border-neutral-200">
              <EmptyState icon={History} title="Pick an entity" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
